"use client";

// Core
import { useTranslations } from "next-intl";
import { useRef } from "react";
import { Link } from "@/i18n/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Grid, Keyboard, Navigation } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { SwiperOptions } from "swiper/types";

// Components
import GameCard from "@/components/GameCard/GameCard";
import IconSprite from "@/components/UI/IconSprite";

// Hooks
import { useGamesFetch } from "@/domains/games/hooks/use-games-fetch";

// Utils
import st from "./index.module.scss";
import { GameTypesType } from "@/domains/games/types";

type GamePreviewSliderProps = {
  filter?: "top" | "new";
  type?: GameTypesType;
  slideRows?: number;
  perPage?: number;
  link?: string;
};

export default function GamesPreview({
  filter,
  type,
  perPage,
  slideRows,
  link,
}: GamePreviewSliderProps) {
  const fetchParams = {
    ...(filter && { [filter]: true }),
    ...(type && { type }),
    ...(perPage && { perPage }),
  };

  const blockTitle = filter ? filter : type;

  const { games } = useGamesFetch(fetchParams);
  const tSliderNav = useTranslations("SliderNav");
  const tSections = useTranslations("Sections");

  const swiperRef = useRef<SwiperType>();

  const SETTINGS_SLIDER: SwiperOptions = {
    slidesPerView: "auto",
    slidesPerGroup: 2,
    watchOverflow: true,
    ...(slideRows && {
      grid: {
        rows: 2,
        fill: "row",
      },
    }),
  };

  if (!games?.length) {
    return null;
  }

  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{tSections(`${blockTitle}.title`)}</h2>
        <div className="slider_navigation popular">
          <div className="slider_navigation__buttons">
            <button
              className="slider_navigation__btn prev"
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <IconSprite name="arrow-1-left" className="slider_navigation__btn_icon" />
            </button>
            <button
              className="slider_navigation__btn next"
              onClick={() => swiperRef.current?.slideNext()}
            >
              <IconSprite name="arrow-1-right" className="slider_navigation__btn_icon" />
            </button>
          </div>

          {link && (
            <Link href={link} className="button slider_navigation__link">
              {tSliderNav("all")}
              <IconSprite name="arrow-1-right" className="slider_navigation__btn_icon" />
            </Link>
          )}
        </div>
      </div>
      <Swiper
        {...SETTINGS_SLIDER}
        modules={[Navigation, Grid, Keyboard]}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {games.map((item, idx) => {
          return (
            <SwiperSlide className={st.slide} key={`${item.slug}+${idx}`}>
              <GameCard inSlide item={item} label={filter && tSections(`${filter}.label`)} />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
}
